<template>
  <div>
    <vx-card title="" :is-filter="true" class="mb-8" :init-collapsed="true" :collapse-action="true">
      <template slot="actions">
        <vs-button color="primary" type="filled" icon-pack="feather" icon="icon-plus" size="small" @click="addTask">{{ $t('add_task') }}</vs-button>
      </template>

      <div class="vx-row">
        <div class="vx-col md:w-1/4 sm:w-1/4 w-full">
          <vs-input :placeholder="$t('name')" v-model="taskSearchParams.name" class="w-full"/>
        </div>
        <div class="vx-col md:w-1/4 sm:w-1/4 w-full">
          <v-select :options="categoryOptions" :reduce="label => label.value" :placeholder="$t('category')" v-model="taskSearchParams.category" class="md:mb-0" />
        </div>
        <div class="vx-col md:w-1/4 sm:w-1/4 w-full">
          <v-select :options="priorityOptions" :reduce="label => label.value" :placeholder="$t('priority')" v-model="taskSearchParams.priority" class="md:mb-0" />
        </div>
        <div class="vx-col md:w-1/4 sm:w-1/4 w-full">
          <v-select :options="progressStatusOptions" :reduce="label => label.value" :placeholder="$t('progress_status')" v-model="taskSearchParams.progress" class="md:mb-0" />
        </div>
      </div>

      <template slot="footer">
        <vs-button color="success" type="filled" class="w-full" @click.prevent="loadTasks(1, null, true)" v-text="$t('search')"></vs-button>
      </template>
    </vx-card>

    <vx-card id="taskDataContainer" :title="$t('tasks')" class="mb-8 vs-con-loading__container">
      <vs-table hover-flat v-if="tasks && tasks.length" :data="tasks">
        <template slot="thead">
          <vs-th v-text="$t('no')"></vs-th>
          <vs-th v-text="$t('name')"></vs-th>
          <vs-th v-text="$t('category')"></vs-th>
          <vs-th v-text="$t('due_date')"></vs-th>
          <vs-th v-text="$t('assigned_users')"></vs-th>
          <vs-th class="text-center" v-text="$t('progress')"></vs-th>
          <vs-th class="text-center" v-text="$t('priority')"></vs-th>
          <vs-th class="text-center" v-text="$t('status')"></vs-th>
          <vs-th class="text-center" v-text="$t('action')"></vs-th>
        </template>

        <template slot-scope="{data}">
          <vs-tr :key="key" v-for="(tr, key) in data">
            <vs-td v-text="tr.count"></vs-td>
            <vs-td v-text="tr.name"></vs-td>
            <vs-td v-text="tr.category_name"></vs-td>
            <vs-td v-text="tr.due_date"></vs-td>
            <vs-td>
              <ul v-if="tr.users.length > 0">
                <li v-for="(user, index) in tr.users" :key="index" v-text="user.name"></li>
              </ul>
              <span v-else>--</span>
            </vs-td>
            <vs-td class="text-center" v-text="`${tr.progress}%`"></vs-td>
            <vs-td class="text-center">
              <badge-priority :content="tr.priority.name" :status="tr.priority.id"></badge-priority>
            </vs-td>
            <vs-td class="text-center">
              <badge-status :content="tr.status.name" :status="tr.status.id"></badge-status>
            </vs-td>
            <vs-td class="text-center">
              <div class="flex justify-center">
                <vs-button color="primary" type="border" icon-pack="feather" icon="icon-edit" size="small" @click.prevent="editTask(tr.id)"></vs-button>&nbsp;
                <vs-button color="danger" type="filled" icon-pack="feather" icon="icon-trash-2" size="small" @click.prevent="deleteTask(tr.id)"></vs-button>
              </div>
            </vs-td>
          </vs-tr>
        </template>
      </vs-table>
      <vs-alert v-else class="flex justify-center p-0 h-auto" color="danger" active="true">
        <div v-text="$t('no_record')"></div>
      </vs-alert>

      <template slot="footer">
        <paginate
          :total="taskPages"
          :itemPerPage="itemPerPage"
          :click-handler="loadTasks"
          v-model="initialPage"
          goto
        >
        </paginate>
      </template>
    </vx-card>

    <modal :title="$t('add_task')" :active.sync="modals.add" size="xl">
      <div class="vx-row">
        <div class="vx-col md:w-1/2 sm:w-1/2 w-full">
          <div class="mb-3">
            <vs-input :label="$t('name')" :placeholder="$t('name')" v-model="taskFormData.name" class="w-full"/>
            <validation-error :model-error="taskFormErrors.name"></validation-error>
          </div>
          <div class="mb-3">
            <label class="vs-input--label" v-text="$t('assigned_to')"></label>
            <v-select :options="userOptions" multiple :reduce="label => label.value" :placeholder="$t('assigned_to')" v-model="taskFormData.assigned_to" class="md:mb-0 hide-selected-item" />
            <validation-error :model-error="taskFormErrors.assigned_to"></validation-error>
          </div>
          <div class="mb-3">
            <label class="vs-input--label" v-text="$t('category')"></label>
            <v-select :options="categoryOptions" :reduce="label => label.value" :placeholder="$t('category')" v-model="taskFormData.task_category_id" class="md:mb-0" />
            <validation-error :model-error="taskFormErrors.task_category_id"></validation-error>
          </div>
          <div class="mb-3">
            <label class="vs-input--label" v-text="$t('due_date')"></label>
            <date-picker v-model="taskFormData.due_date" :model="taskFormData.due_date" :placeholder="$t('due_date')"></date-picker>
            <validation-error :model-error="taskFormErrors.due_date"></validation-error>
          </div>

          <vs-divider position="center" border-style="dashed">Additional Information</vs-divider>

          <div class="vx-row">
            <div class="vx-col md:w-1/2 sm:w-1/2 w-full">
              <label class="vs-input--label" v-text="$t('start_date')"></label>
              <date-picker v-model="taskFormData.start_date" :model="taskFormData.start_date" :placeholder="$t('start_date')"></date-picker>
              <validation-error :model-error="taskFormErrors.start_date"></validation-error>
            </div>

            <div class="vx-col md:w-1/2 sm:w-1/2 w-full">
              <label class="vs-input--label" v-text="$t('reminder_date')"></label>
              <date-picker v-model="taskFormData.reminder_date" :model="taskFormData.reminder_date" :placeholder="$t('reminder_date')"></date-picker>
              <validation-error :model-error="taskFormErrors.reminder_date"></validation-error>
            </div>
          </div>

          <div class="mb-3">
            <label class="vs-input--label flex align-center">
              <span class="mr-2">{{ $t('progress') }}:</span>
              <vs-chip color="primary" class="float-none p-2" v-text="`${taskFormData.progress}%`"></vs-chip>
            </label>
            <div class="p-1">
              <vs-slider v-model="taskFormData.progress" />
            </div>
          </div>
        </div>

        <div class="vx-col md:w-1/2 sm:w-1/2 w-full">
          <div class="mb-3">
            <label class="vs-input--label" v-text="$t('priority')"></label>
            <v-select :options="priorityOptions" :reduce="label => label.value" :placeholder="$t('priority')" v-model="taskFormData.priority_id" class="md:mb-0" />
            <validation-error :model-error="taskFormErrors.priority_id"></validation-error>
          </div>

          <div class="mb-3">
            <label class="vs-input--label" v-text="$t('progress_status')"></label>
            <v-select :options="progressStatusOptions" :reduce="label => label.value" :placeholder="$t('progress_status')" v-model="taskFormData.progress_status_id" class="md:mb-0" />
            <validation-error :model-error="taskFormErrors.progress_status_id"></validation-error>
          </div>

          <div class="mb-3">
            <label class="vs-input--label" v-text="$t('related_to')"></label>
            <v-select :options="projectOptions" :reduce="label => label.value" :placeholder="$t('related_to')" v-model="taskFormData.related_to" class="md:mb-0" />
            <validation-error :model-error="taskFormErrors.related_to"></validation-error>
          </div>

          <div class="mb-3">
            <vs-input :label="$tc('link', 2)" :placeholder="$tc('link', 2)" v-model="taskFormData.links" class="w-full"/>
            <validation-error :model-error="taskFormErrors.links"></validation-error>
          </div>

          <div class="mb-3">
            <label class="vs-input--label" v-text="$t('visibility')"></label>
            <v-select :options="visibilityOptions" :reduce="label => label.value" :placeholder="$t('visibility')" v-model="taskFormData.visibility" class="md:mb-0" />
            <validation-error :model-error="taskFormErrors.visibility"></validation-error>
          </div>

          <div class="mb-3">
            <label class="vs-input--label" v-text="$t('description')"></label>
            <vs-textarea class="mb-0" v-model="taskFormData.description" :placeholder="$t('description')" />
            <validation-error :model-error="taskFormErrors.description"></validation-error>
          </div>
        </div>
      </div>

      <vs-divider></vs-divider>

      <div class="flex justify-end">
        <vs-button id="addTaskButton" color="primary" type="filled" @click.prevent="submitTask">{{ $t('save') }}</vs-button>
      </div>
    </modal>

    <modal :title="$t('edit_task')" :active.sync="modals.edit" size="xl">
      <div id="taskEditFormDataContainer" class="vs-con-loading__container">
        <div class="vx-row">
          <div class="vx-col md:w-1/2 sm:w-1/2 w-full">
            <div class="mb-3">
              <vs-input :label="$t('name')" :placeholder="$t('name')" v-model="taskFormData.name" class="w-full"/>
              <validation-error :model-error="taskFormErrors.name"></validation-error>
            </div>
            <div class="mb-3">
              <label class="vs-input--label" v-text="$t('assigned_to')"></label>
              <v-select :options="userOptions" multiple :reduce="label => label.value" :placeholder="$t('assigned_to')" v-model="taskFormData.assigned_to" class="md:mb-0 hide-selected-item" />
              <validation-error :model-error="taskFormErrors.assigned_to"></validation-error>
            </div>
            <div class="mb-3">
              <label class="vs-input--label" v-text="$t('category')"></label>
              <v-select :options="categoryOptions" :reduce="label => label.value" :placeholder="$t('category')" v-model="taskFormData.task_category_id" class="md:mb-0" />
              <validation-error :model-error="taskFormErrors.task_category_id"></validation-error>
            </div>
            <div class="mb-3">
              <label class="vs-input--label" v-text="$t('due_date')"></label>
              <date-picker v-model="taskFormData.due_date" :model="taskFormData.due_date" :placeholder="$t('due_date')"></date-picker>
              <validation-error :model-error="taskFormErrors.due_date"></validation-error>
            </div>

            <vs-divider position="center" border-style="dashed">Additional Information</vs-divider>

            <div class="vx-row">
              <div class="vx-col md:w-1/2 sm:w-1/2 w-full">
                <label class="vs-input--label" v-text="$t('start_date')"></label>
                <date-picker v-model="taskFormData.start_date" :model="taskFormData.start_date" :placeholder="$t('start_date')"></date-picker>
                <validation-error :model-error="taskFormErrors.start_date"></validation-error>
              </div>

              <div class="vx-col md:w-1/2 sm:w-1/2 w-full">
                <label class="vs-input--label" v-text="$t('reminder_date')"></label>
                <date-picker v-model="taskFormData.reminder_date" :model="taskFormData.reminder_date" :placeholder="$t('reminder_date')"></date-picker>
                <validation-error :model-error="taskFormErrors.reminder_date"></validation-error>
              </div>
            </div>

            <div class="mb-3">
              <label class="vs-input--label flex align-center">
                <span class="mr-2">{{ $t('progress') }}:</span>
                <vs-chip color="primary" class="float-none p-2" v-text="`${taskFormData.progress}%`"></vs-chip>
              </label>
              <div class="p-1">
                <vs-slider v-model="taskFormData.progress" />
              </div>
            </div>
          </div>

          <div class="vx-col md:w-1/2 sm:w-1/2 w-full">
            <div class="mb-3">
              <label class="vs-input--label" v-text="$t('priority')"></label>
              <v-select :options="priorityOptions" :reduce="label => label.value" :placeholder="$t('priority')" v-model="taskFormData.priority_id" class="md:mb-0" />
              <validation-error :model-error="taskFormErrors.priority_id"></validation-error>
            </div>

            <div class="mb-3">
              <label class="vs-input--label" v-text="$t('progress_status')"></label>
              <v-select :options="progressStatusOptions" :reduce="label => label.value" :placeholder="$t('progress_status')" v-model="taskFormData.progress_status_id" class="md:mb-0" />
              <validation-error :model-error="taskFormErrors.progress_status_id"></validation-error>
            </div>

            <div class="mb-3">
              <label class="vs-input--label" v-text="$t('related_to')"></label>
              <v-select :options="projectOptions" :reduce="label => label.value" :placeholder="$t('related_to')" v-model="taskFormData.related_to" class="md:mb-0" />
              <validation-error :model-error="taskFormErrors.related_to"></validation-error>
            </div>

            <div class="mb-3">
              <vs-input :label="$tc('link', 2)" :placeholder="$tc('link', 2)" v-model="taskFormData.links" class="w-full"/>
              <validation-error :model-error="taskFormErrors.links"></validation-error>
            </div>

            <div class="mb-3">
              <label class="vs-input--label" v-text="$t('visibility')"></label>
              <v-select :options="visibilityOptions" :reduce="label => label.value" :placeholder="$t('visibility')" v-model="taskFormData.visibility" class="md:mb-0" />
              <validation-error :model-error="taskFormErrors.visibility"></validation-error>
            </div>

            <div class="mb-3">
              <label class="vs-input--label" v-text="$t('description')"></label>
              <vs-textarea class="mb-0" v-model="taskFormData.description" :placeholder="$t('description')" />
              <validation-error :model-error="taskFormErrors.description"></validation-error>
            </div>
          </div>
        </div>


        <vs-divider></vs-divider>

        <div class="flex justify-end">
          <vs-button id="editTaskButton" color="primary" type="filled" @click.prevent="updateTask">{{ $t('save') }}</vs-button>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
// TODO: Make Shared Component for List, Add and Edit (Admin, User Usage)
import Paginate from '@/components/Paginate'
import Modal from '@/components/Modal'
import Pagination from '@/mixins/paginate'
import Task from '@/models/Task'
import BadgePriority from '@/components/badge/Priority'
import BadgeStatus from '@/components/badge/Status'
import DatePicker from '@/components/form-inputs/DatePicker'

export default {
  name: 'Admin.Tasks',
  mixins: [Pagination],
  components: {
    Paginate,
    Modal,
    BadgePriority,
    BadgeStatus,
    DatePicker
  },
  data () {
    return {
      tasks: {},
      taskPages: 0,
      categoryOptions: [],
      progressStatusOptions: [],
      priorityOptions: [],
      visibilityOptions: [],
      userOptions: [],
      projectOptions: [],
      taskFormData: {
        task_category_id: '',
        name: '',
        description: '',
        due_date: '',
        start_date: '',
        reminder_date: '',
        progress: 0,
        priority_id: '',
        progress_status_id: '',
        related_to: '',
        visibility: 0,
        assigned_to: [],
        links: ''
      },
      taskFormErrors: [],
      taskSearchParams: {
        name: '',
        category: '',
        priority: '',
        progress: ''
      },
      modals: {
        add: false,
        edit: false
      }
    }
  },
  watch: {
    'modals.add' (val) {
      if (!val) {
        this.resetTaskForm()
      }
    },
    'modals.edit' (val) {
      if (!val) {
        this.resetTaskForm()
      }
    }
  },
  mounted () {
    this.loadTasks(this.initialPage, null)
  },
  methods: {
    async loadTasks (page = 1, itemPerPage = null, backToPageOne = false) {
      this.$vs.loading({container: '#taskDataContainer'})
      if (itemPerPage !== null) {
        this.itemPerPage = itemPerPage
      } else if (this.itemPerPage !== null) {
        itemPerPage = this.itemPerPage
      }
      const response = await Task.findAll({ ...this.taskSearchParams, page, itemPerPage })
      this.tasks = response.data.data.tasks
      this.taskPages = response.data.meta.pagination.last_page

      this.categoryOptions = response.data.meta.options.categories
      this.progressStatusOptions = response.data.meta.options.progressStatuses
      this.priorityOptions = response.data.meta.options.priorities
      this.visibilityOptions = response.data.meta.options.visibilities
      this.userOptions = response.data.meta.options.users
      this.projectOptions = response.data.meta.options.projects
      this.itemPerPage = response.data.meta.pagination.per_page
      if (backToPageOne === true) {
        this.initialPage = 1
      } else {
        this.initialPage = page
      }
      this.$vs.loading.close('#taskDataContainer > .con-vs-loading')
    },
    addTask () {
      this.modals.add = true
    },
    async submitTask () {
      this.$vs.loading({background: 'primary', color: '#fff', container: '#addTaskButton', scale: 0.45})

      try {
        const response = await Task.store(this.taskFormData)

        this.$swalHelper.success({
          title: this.$t('action_success'),
          text: this.$t(response.data),
          confirmButton: this.$t('confirm')
        })

        this.modals.add = false

        this.loadTasks(1, null)
        this.resetTaskForm()
      } catch (e) {
        const errors = this.$swalHelper.displayCatchError({
          title: 'Error!',
          errorObject: e,
          confirmButton: this.$t('confirm')
        })
        this.taskFormErrors = errors.validationErrors
      }

      this.$vs.loading.close('#addTaskButton > .con-vs-loading')
    },
    async editTask (id) {
      this.$vs.loading({container: '#taskEditFormDataContainer'})

      try {
        this.modals.edit = true
        const response = await Task.view({ id })
        this.taskFormData = response.data.data
      } catch (e) {
        this.$swalHelper.displayCatchError({
          title: 'Error!',
          errorObject: e,
          confirmButton: this.$t('confirm')
        })
      }

      this.$vs.loading.close('#taskEditFormDataContainer > .con-vs-loading')
    },
    async updateTask () {
      this.$vs.loading({background: 'primary', color: '#fff', container: '#editTaskButton', scale: 0.45})

      try {
        const response = await Task.update(this.taskFormData)

        this.$swalHelper.success({
          title: this.$t('action_success'),
          text: this.$t(response.data),
          confirmButton: this.$t('confirm')
        })

        this.modals.edit = false

        this.loadTasks()
        this.resetTaskForm()
      } catch (e) {
        const errors = this.$swalHelper.displayCatchError({
          title: 'Error!',
          errorObject: e,
          confirmButton: this.$t('confirm')
        })
        this.taskFormErrors = errors.validationErrors
      }

      this.$vs.loading.close('#editTaskButton > .con-vs-loading')
    },
    async deleteTask (id) {
      const confirm = await this.$swalHelper.warning({
        title: this.$t('are_you_sure_you_want_to_delete'),
        confirmButton: this.$t('execution'),
        cancelButton: this.$t('cancel')
      })

      if (confirm.value) {
        try {
          const response = await Task.destroy({ id })

          this.$swalHelper.success({
            title: this.$t('action_success'),
            text: this.$t(response.data),
            confirmButton: this.$t('confirm')
          })

          this.loadTasks(1, null)
        } catch (e) {
          this.$swalHelper.displayCatchError({
            title: 'Error!',
            errorObject: e,
            confirmButton: this.$t('confirm')
          })
        }
      }
    },
    resetTaskForm () {
      this.taskFormData = {
        task_category_id: '',
        name: '',
        description: '',
        due_date: '',
        start_date: '',
        reminder_date: '',
        progress: 0,
        priority_id: '',
        progress_status_id: '',
        related_to: '',
        visibility: 0,
        assigned_to: [],
        links: ''
      }
      this.taskFormErrors = []
    }
  }
}
</script>

<style lang="scss">
  #addTaskButton,
  #editTaskButton {
    .con-vs-loading {
      position: absolute !important;
    }
  }
</style>
